import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetAlphaBscResearchProgressQuery } from "@/store/api";

/** Live per-step progress indicator. Polls the backend every 1s while
 *  the research API call is in flight, reading from the
 *  `deep_research_progress` table which the analyze() function writes
 *  to at each step boundary. Falls back to elapsed-time-only display
 *  when no backend progress is available yet. */
function ResearchProgress({ tokenId, active }:
  { tokenId: number | null; active: boolean }) {
  const progress = useGetAlphaBscResearchProgressQuery(
    (tokenId ?? -1) as number,
    {
      skip: !active || !tokenId,
      pollingInterval: active ? 1000 : 0,
      refetchOnMountOrArgChange: true,
    },
  );
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((s) => s + 0.5), 500);
    return () => clearInterval(t);
  }, [active]);

  const steps: Array<{ step_order: number; step_name: string; status: string;
    started_at?: string; finished_at?: string; detail?: string }> =
    progress.data?.steps || [];
  const jobStatus: string | undefined = progress.data?.job_status;

  // Resolve current step: last step with status='running', or first
  // pending if no running rows, or all-done.
  const runningIdx = steps.findIndex((s) => s.status === "running");
  const doneCount = steps.filter((s) => s.status === "done").length;

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-fg font-mono tabular-nums">
          {Math.floor(elapsed)}s elapsed
        </span>
        {steps.length > 0 ? (
          <span className="text-muted">
            {doneCount}/{steps.length} steps complete
            {runningIdx >= 0 && (
              <> · currently <span className="text-fg">step {runningIdx + 1}</span></>
            )}
          </span>
        ) : (
          <span className="text-muted italic">waiting for first step …</span>
        )}
        {jobStatus === "error" && (
          <Badge tone="bad">job failed</Badge>
        )}
      </div>
      {steps.length === 0 ? (
        <div className="text-muted italic">
          No live progress yet — research is initializing. Steps will
          appear here within a couple of seconds.
        </div>
      ) : (
        <ul className="space-y-1">
          {steps.map((s) => {
            const tone =
              s.status === "done" ? "text-green-500" :
              s.status === "running" ? "text-fg" :
              s.status === "error" ? "text-red-500" :
              "text-muted/60";
            const mark =
              s.status === "done" ? "✓" :
              s.status === "running" ? "⏳" :
              s.status === "error" ? "✗" :
              "·";
            const dur =
              s.started_at && s.finished_at
                ? `${((new Date(s.finished_at).getTime() - new Date(s.started_at).getTime()) / 1000).toFixed(1)}s`
                : "";
            return (
              <li key={s.step_order}
                  className={`flex items-baseline gap-2 ${tone}`}>
                <span className={`font-mono w-4 ${s.status === "running" ? "animate-pulse" : ""}`}>
                  {mark}
                </span>
                <span className="flex-1">{s.step_name}</span>
                {dur && <span className="text-muted text-[10px] tabular-nums">{dur}</span>}
                {s.detail && s.status === "error" && (
                  <span className="text-red-400 text-[10px]">{s.detail}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {elapsed > 75 && (
        <div className="mt-2 text-yellow-500 italic">
          Taking longer than expected — Gemini Pro may be rate-limited or
          a third-party API hit a 429. Result will still arrive.
        </div>
      )}
    </div>
  );
}

/**
 * DeepResearchDialog — renders the multi-step pre-pump research report.
 *
 * Sections:
 *  1. Gemini verdict (GO/WAIT/SKIP + confidence + thesis/risk)
 *  2. Checklist (7 boolean gates)
 *  3. Multi-TF structure (weekly / daily / 4H / 1H)
 *  4. Historical analogs (180d backtest of similar setups)
 *  5. Relative strength vs BTC
 *  6. Volume quality
 *  7. Orderbook depth
 *  8. Entry plan + invalidation
 *  9. Missing external data (TODO badges)
 */
export function DeepResearchDialog({
  symbol, tokenId, open, onClose, report, loading,
}: {
  symbol: string;
  tokenId?: number | null;
  open: boolean;
  onClose: () => void;
  report: any | null;
  loading: boolean;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-mono">{symbol}</span>
          <span className="text-sm font-normal text-muted">Deep research report</span>
          {report?._from_cache && (
            <Badge tone="muted">cached ≤15min</Badge>
          )}
        </div>
      }
    >
      {loading && (
        <div className="space-y-4">
          <div className="text-xs text-muted">
            Running deep analysis. Typically 40-60s on a fresh run; instant
            when cached within 15min. Progress below is approximate — the
            backend returns the full report in one go, so step indicators
            are time-based, not stream-based.
          </div>
          <div className="rounded border border-border/60 p-3 bg-bg2">
            <ResearchProgress tokenId={tokenId ?? null} active={loading} />
          </div>
          <Skeleton className="h-12 w-full opacity-50" />
          <Skeleton className="h-20 w-full opacity-50" />
        </div>
      )}

      {!loading && !report && (
        <div className="text-sm text-muted">No report yet.</div>
      )}

      {!loading && report?.status === "error" && (
        <div className="text-sm text-red-500">
          Error: {report.reason ?? "unknown"}
        </div>
      )}

      {!loading && report?.gemini_verdict && (
        <ReportBody r={report} />
      )}

      {!loading && report && !report.gemini_verdict && !report.reason && (
        <div className="text-sm text-yellow-500">
          Technical data computed but Gemini synthesis failed:
          <pre className="text-xs mt-2">{report.gemini_error ?? "unknown"}</pre>
        </div>
      )}
    </Dialog>
  );
}

function ReportBody({ r }: { r: any }) {
  const v = r.gemini_verdict;
  const cl = v.checklist || {};
  const verdictTone = v.decision === "GO" ? "good"
                     : v.decision === "WAIT" ? "warn"
                                              : "bad";

  return (
    <div className="space-y-4 text-sm">
      {/* Verdict header */}
      <div className="rounded border p-3 bg-muted/10">
        <div className="flex items-baseline gap-3 flex-wrap mb-2">
          <Badge tone={verdictTone as any}>{v.decision}</Badge>
          <span className="font-semibold">confidence {v.confidence}/100</span>
          <span className="text-muted text-xs">
            current ${Number(r.current_price).toFixed(4)} ({r.ticker_24h_change_pct >= 0 ? "+" : ""}{r.ticker_24h_change_pct}% 24h)
          </span>
          <span className="text-muted text-xs ml-auto">
            {r.gemini_model} · {r.total_latency_ms}ms
          </span>
        </div>
        <div className="space-y-1">
          <div><span className="text-muted">Thesis:</span> {v.key_thesis}</div>
          <div><span className="text-muted">Risk:</span>   {v.key_risk}</div>
          <div>
            <span className="text-muted">Entry:</span>{" "}
            <Badge tone="info">{v.entry_recommendation}</Badge>
            <span className="ml-3 text-xs text-muted">size: {v.size_recommendation_pct_of_normal}% of normal</span>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div>
        <div className="font-semibold mb-2">Checklist</div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          {Object.entries(cl).map(([k, val]) => (
            <div key={k} className="flex items-center gap-2">
              <span className={val ? "text-green-500" : "text-red-500"}>
                {val ? "✓" : "✗"}
              </span>
              <span className="font-mono">{k}</span>
            </div>
          ))}
        </div>
      </div>

      {/* What to watch */}
      <div>
        <div className="font-semibold mb-2">What to watch</div>
        <ul className="list-disc list-inside space-y-1 text-xs">
          {(v.what_to_watch || []).map((w: string, i: number) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>

      {/* Structure */}
      <div>
        <div className="font-semibold mb-2">Multi-timeframe structure</div>
        <table className="w-full text-xs">
          <thead className="text-muted">
            <tr>
              <th className="text-left">TF</th>
              <th className="text-left">Trend</th>
              <th className="text-right">RSI</th>
              <th className="text-right">ATR%</th>
              <th className="text-right">vs SMA20</th>
              <th className="text-center">EMA stacked</th>
            </tr>
          </thead>
          <tbody>
            {(["weekly","daily","h4","h1"] as const).map((tf) => {
              const s = r.structure?.[tf];
              if (!s || s.error) {
                return (
                  <tr key={tf}>
                    <td className="font-mono">{tf}</td>
                    <td colSpan={5} className="text-muted">{s?.error ?? "—"}</td>
                  </tr>
                );
              }
              const trendTone = s.trend === "uptrend" ? "good"
                              : s.trend === "downtrend" ? "bad"
                                                          : "muted";
              return (
                <tr key={tf}>
                  <td className="font-mono">{tf}</td>
                  <td><Badge tone={trendTone as any}>{s.trend}</Badge></td>
                  <td className="text-right tabular-nums">{s.rsi_14}</td>
                  <td className="text-right tabular-nums">{s.atr_pct}%</td>
                  <td className="text-right tabular-nums">{s.above_sma20_pct}%</td>
                  <td className="text-center">{s.stacked_bull ? "✓" : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Analogs */}
      <div>
        <div className="font-semibold mb-2">Historical analogs (180d, RSI 40-70 + vol ≥1.5x)</div>
        {r.historical_analogs_180d?.count ? (
          <div className="text-xs grid grid-cols-3 gap-2">
            <div><span className="text-muted">Count:</span> {r.historical_analogs_180d.count}</div>
            <div><span className="text-muted">Win rate 7d:</span> {r.historical_analogs_180d.win_rate_7d_pct}%</div>
            <div><span className="text-muted">Avg ret 7d:</span> {r.historical_analogs_180d.avg_ret_7d_pct}%</div>
            <div><span className="text-muted">Best:</span> +{r.historical_analogs_180d.best_ret_pct}%</div>
            <div><span className="text-muted">Worst:</span> {r.historical_analogs_180d.worst_ret_pct}%</div>
            <div><span className="text-muted">Avg max gain:</span> {r.historical_analogs_180d.avg_max_gain_pct}%</div>
          </div>
        ) : (
          <div className="text-xs text-muted">
            {r.historical_analogs_180d?.note ?? r.historical_analogs_180d?.error}
          </div>
        )}
      </div>

      {/* Rel strength */}
      <div>
        <div className="font-semibold mb-2">Relative strength vs BTC</div>
        <div className="text-xs grid grid-cols-2 gap-x-4 gap-y-1">
          <div>7d: <span className="tabular-nums">{r.relative_strength?.symbol_7d_pct}%</span> vs BTC {r.relative_strength?.btc_7d_pct}% — <strong className={r.relative_strength?.outperforms_btc_7d ? "text-green-500" : "text-red-500"}>spread {r.relative_strength?.spread_7d_pct}%</strong></div>
          <div>30d: <span className="tabular-nums">{r.relative_strength?.symbol_30d_pct}%</span> vs BTC {r.relative_strength?.btc_30d_pct}% — <strong className={r.relative_strength?.outperforms_btc_30d ? "text-green-500" : "text-red-500"}>spread {r.relative_strength?.spread_30d_pct}%</strong></div>
        </div>
      </div>

      {/* Volume + Orderbook */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="font-semibold mb-2">Volume quality</div>
          <div className="text-xs space-y-1">
            <div><span className="text-muted">24h USD:</span> ${Number(r.volume_quality?.vol_24h_usd ?? 0).toLocaleString()}</div>
            <div><span className="text-muted">vs 30d avg:</span> {r.volume_quality?.vs_30d_avg_x}x</div>
            <div><span className="text-muted">Last 7d vs prev 7d:</span> {r.volume_quality?.last_7d_vs_prev_7d_x}x</div>
            <div><span className="text-muted">Trend:</span> <Badge tone="info">{r.volume_quality?.trend_7d}</Badge></div>
          </div>
        </div>
        <div>
          <div className="font-semibold mb-2">Orderbook (5% depth)</div>
          <div className="text-xs space-y-1">
            <div><span className="text-muted">Spread:</span> {r.orderbook?.spread_bps} bps</div>
            <div><span className="text-muted">Bid depth:</span> ${Number(r.orderbook?.bid_depth_5pct_usd ?? 0).toLocaleString()}</div>
            <div><span className="text-muted">Ask depth:</span> ${Number(r.orderbook?.ask_depth_5pct_usd ?? 0).toLocaleString()}</div>
            <div><span className="text-muted">Imbalance:</span>{" "}
              <span className={r.orderbook?.imbalance > 0.1 ? "text-green-500" : r.orderbook?.imbalance < -0.1 ? "text-red-500" : ""}>
                {r.orderbook?.imbalance}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tokenomics */}
      {r.tokenomics ? (
        <div>
          <div className="font-semibold mb-2">
            Tokenomics{" "}
            <span className="text-xs text-muted font-normal">
              · CoinGecko (rank #{r.tokenomics.market_cap_rank ?? "—"})
              {r.tokenomics._cache_age_s !== undefined && (
                <> · cached {Math.floor(r.tokenomics._cache_age_s / 60)}m</>
              )}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-1">
              <div>
                <span className="text-muted">FDV/mcap:</span>{" "}
                <strong>{r.tokenomics.fdv_mcap_ratio ?? "—"}x</strong>{" "}
                <Badge tone={
                  r.tokenomics.overhang_tag === "low_overhang" ? "good"
                  : r.tokenomics.overhang_tag === "moderate_overhang" ? "info"
                  : r.tokenomics.overhang_tag === "significant_overhang" ? "warn"
                  : r.tokenomics.overhang_tag === "major_overhang" ? "bad"
                  : "muted"
                }>{r.tokenomics.overhang_tag}</Badge>
              </div>
              <div>
                <span className="text-muted">Market cap:</span>{" "}
                ${Number(r.tokenomics.market_cap_usd ?? 0).toLocaleString()}
              </div>
              <div>
                <span className="text-muted">FDV:</span>{" "}
                ${Number(r.tokenomics.fdv_usd ?? 0).toLocaleString()}
              </div>
              <div>
                <span className="text-muted">Circulating supply:</span>{" "}
                <strong>{Number(r.tokenomics.circulating_supply ?? 0).toLocaleString()}</strong>
                {r.tokenomics.circulating_pct_of_total !== null && (
                  <Badge tone={
                    r.tokenomics.circulating_pct_of_total >= 70 ? "good"
                    : r.tokenomics.circulating_pct_of_total >= 40 ? "info"
                    : r.tokenomics.circulating_pct_of_total >= 20 ? "warn"
                    : "bad"
                  } className="ml-1">
                    {r.tokenomics.circulating_pct_of_total}% of total
                  </Badge>
                )}
              </div>
              <div>
                <span className="text-muted">Total supply:</span>{" "}
                <strong>{r.tokenomics.total_supply
                  ? Number(r.tokenomics.total_supply).toLocaleString()
                  : "—"}</strong>
                <span className="text-muted text-[10px] ml-1">(existing tokens incl. locked/vested)</span>
              </div>
              <div>
                <span className="text-muted">Max supply:</span>{" "}
                <strong>{r.tokenomics.max_supply
                  ? Number(r.tokenomics.max_supply).toLocaleString()
                  : <span className="text-yellow-500">uncapped</span>}</strong>
                <span className="text-muted text-[10px] ml-1">(hard ceiling)</span>
              </div>
              {r.tokenomics.total_supply && r.tokenomics.circulating_supply && (
                <div className="text-[11px] mt-1 pt-1 border-t border-border/30">
                  <span className="text-muted">Locked/vested:</span>{" "}
                  <strong className={(r.tokenomics.total_supply - r.tokenomics.circulating_supply) / r.tokenomics.total_supply > 0.5 ? "text-rose-400" : ""}>
                    {Number(r.tokenomics.total_supply - r.tokenomics.circulating_supply).toLocaleString()}
                  </strong>
                  <span className="text-muted ml-1">
                    ({(((r.tokenomics.total_supply - r.tokenomics.circulating_supply) / r.tokenomics.total_supply) * 100).toFixed(1)}% sell-pressure overhang)
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <div>
                <span className="text-muted">ATH:</span>{" "}
                ${r.tokenomics.ath_usd}
                {r.tokenomics.ath_date && (
                  <span className="text-muted"> ({r.tokenomics.ath_date.slice(0, 10)})</span>
                )}
              </div>
              <div>
                <span className="text-muted">From ATH:</span>{" "}
                <strong className={
                  (r.tokenomics.ath_change_pct ?? 0) > -5 ? "text-red-500"
                  : (r.tokenomics.ath_change_pct ?? 0) > -50 ? "text-yellow-500"
                  : "text-green-500"
                }>{r.tokenomics.ath_change_pct}%</strong>{" "}
                <Badge tone={
                  r.tokenomics.ath_zone === "at_or_near_ath_high_caution" ? "bad"
                  : r.tokenomics.ath_zone === "within_25pct_of_ath" ? "warn"
                  : r.tokenomics.ath_zone === "down_75pct_plus_deep_value_or_dead" ? "muted"
                  : "info"
                }>{r.tokenomics.ath_zone}</Badge>
              </div>
              <div>
                <span className="text-muted">14d / 30d / 60d / 1y:</span>{" "}
                <span className="tabular-nums">
                  {Number(r.tokenomics.price_change_pct_14d ?? 0).toFixed(1)}% /{" "}
                  {Number(r.tokenomics.price_change_pct_30d ?? 0).toFixed(1)}% /{" "}
                  {Number(r.tokenomics.price_change_pct_60d ?? 0).toFixed(1)}% /{" "}
                  {Number(r.tokenomics.price_change_pct_1y ?? 0).toFixed(1)}%
                </span>
              </div>
              <div>
                <span className="text-muted">Categories:</span>{" "}
                {(r.tokenomics.categories || []).map((c: string) => (
                  <Badge key={c} tone="muted" className="mr-1">{c}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-xs text-yellow-500">
          ⚠ Tokenomics lookup failed (CoinGecko symbol not resolved or
          API hiccup). Gemini knows it's flying blind on supply.
        </div>
      )}

      {/* TVL (DeFiLlama) */}
      {r.tvl_metrics ? (
        <div>
          <div className="font-semibold mb-2">
            Ecosystem TVL{" "}
            <span className="text-xs text-muted font-normal">
              · DeFiLlama
              {r.tvl_metrics._cache_age_s !== undefined && (
                <> · cached {Math.floor(r.tvl_metrics._cache_age_s / 60)}m</>
              )}
            </span>
          </div>
          {r.tvl_metrics.available ? (
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="space-y-1">
                <div>
                  <span className="text-muted">{r.tvl_metrics.lookup_type === "chain" ? "Chain" : "Protocol"}:</span>{" "}
                  <strong>{r.tvl_metrics.name}</strong>
                </div>
                <div>
                  <span className="text-muted">Current TVL:</span>{" "}
                  ${Number(r.tvl_metrics.current_tvl_usd ?? 0).toLocaleString()}
                </div>
                <div>
                  <span className="text-muted">Trend:</span>{" "}
                  <Badge tone={
                    r.tvl_metrics.tvl_trend_tag === "accelerating_up" ? "good"
                    : r.tvl_metrics.tvl_trend_tag === "up" ? "info"
                    : r.tvl_metrics.tvl_trend_tag === "flat" ? "muted"
                    : r.tvl_metrics.tvl_trend_tag === "down" ? "warn"
                    : r.tvl_metrics.tvl_trend_tag === "accelerating_down" ? "bad"
                    : "muted"
                  }>{r.tvl_metrics.tvl_trend_tag}</Badge>
                </div>
                {r.tvl_metrics.mcap_to_tvl_ratio !== undefined && (
                  <div>
                    <span className="text-muted">Mcap/TVL:</span>{" "}
                    <strong>{r.tvl_metrics.mcap_to_tvl_ratio}x</strong>{" "}
                    <Badge tone={
                      r.tvl_metrics.mcap_to_tvl_tag === "low_valuation_vs_usage" ? "good"
                      : r.tvl_metrics.mcap_to_tvl_tag === "moderate_valuation_vs_usage" ? "info"
                      : r.tvl_metrics.mcap_to_tvl_tag === "rich_valuation_vs_usage" ? "warn"
                      : "bad"
                    }>{r.tvl_metrics.mcap_to_tvl_tag}</Badge>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <div>
                  <span className="text-muted">7d change:</span>{" "}
                  <span className={(r.tvl_metrics.tvl_change_7d_pct ?? 0) >= 0 ? "text-green-500" : "text-red-500"}>
                    {r.tvl_metrics.tvl_change_7d_pct >= 0 ? "+" : ""}{r.tvl_metrics.tvl_change_7d_pct}%
                  </span>
                </div>
                <div>
                  <span className="text-muted">30d change:</span>{" "}
                  <span className={(r.tvl_metrics.tvl_change_30d_pct ?? 0) >= 0 ? "text-green-500" : "text-red-500"}>
                    {r.tvl_metrics.tvl_change_30d_pct >= 0 ? "+" : ""}{r.tvl_metrics.tvl_change_30d_pct}%
                  </span>
                </div>
                <div>
                  <span className="text-muted">90d change:</span>{" "}
                  <span className={(r.tvl_metrics.tvl_change_90d_pct ?? 0) >= 0 ? "text-green-500" : "text-red-500"}>
                    {r.tvl_metrics.tvl_change_90d_pct >= 0 ? "+" : ""}{r.tvl_metrics.tvl_change_90d_pct}%
                  </span>
                </div>
                {r.tvl_metrics.category && (
                  <div>
                    <span className="text-muted">Category:</span>{" "}
                    <Badge tone="muted">{r.tvl_metrics.category}</Badge>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted italic">
              {r.tvl_metrics.reason === "no_defi_exposure_by_design"
                ? "No DeFi ecosystem signal (meme/PoW token) — neutral, not a negative."
                : `TVL lookup failed: ${r.tvl_metrics.reason}`}
            </div>
          )}
        </div>
      ) : null}

      {/* Catalyst News */}
      {r.catalyst_news ? (
        <div>
          <div className="font-semibold mb-2">
            Catalyst news{" "}
            <span className="text-xs text-muted font-normal">
              · Gemini search · last {r.catalyst_news.lookback_days ?? 14}d
              {r.catalyst_news._cache_age_s !== undefined && (
                <> · cached {Math.floor(r.catalyst_news._cache_age_s / 60)}m</>
              )}
              <Badge tone={
                r.catalyst_news.overall_sentiment === "positive" ? "good"
                : r.catalyst_news.overall_sentiment === "negative" ? "bad"
                : r.catalyst_news.overall_sentiment === "mixed" ? "warn"
                : "muted"
              } className="ml-2">
                {r.catalyst_news.overall_sentiment}
              </Badge>
            </span>
          </div>
          {r.catalyst_news.summary && (
            <div className="text-xs italic mb-2 text-muted">
              {r.catalyst_news.summary}
            </div>
          )}
          {(r.catalyst_news.recent_catalysts?.length ?? 0) > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold mb-1">Recent (last {r.catalyst_news.lookback_days ?? 14} days)</div>
              <ul className="space-y-1 text-xs">
                {r.catalyst_news.recent_catalysts.map((c: any, i: number) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="text-muted font-mono text-[10px] whitespace-nowrap">{c.date}</span>
                    <Badge tone={
                      c.sentiment === "positive" ? "good"
                      : c.sentiment === "negative" ? "bad"
                      : "muted"
                    }>{c.significance}</Badge>
                    <Badge tone="muted" className="text-[10px]">{c.category}</Badge>
                    <span>{c.headline}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(r.catalyst_news.upcoming_events?.length ?? 0) > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold mb-1">Upcoming</div>
              <ul className="space-y-1 text-xs">
                {r.catalyst_news.upcoming_events.map((e: any, i: number) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="text-muted font-mono text-[10px] whitespace-nowrap">{e.date}</span>
                    <Badge tone={e.significance === "high" ? "warn" : "muted"}>
                      {e.significance}
                    </Badge>
                    <Badge tone="muted" className="text-[10px]">{e.category}</Badge>
                    <span>{e.event}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(r.catalyst_news.sources?.length ?? 0) > 0 && (
            <div className="text-[10px] text-muted">
              Sources:{" "}
              {r.catalyst_news.sources.map((s: any, i: number) => (
                <span key={i}>
                  <a href={s.uri} target="_blank" rel="noopener noreferrer"
                     className="underline hover:text-foreground">
                    {(s.title || s.uri).slice(0, 30)}
                  </a>
                  {i < r.catalyst_news.sources.length - 1 && " · "}
                </span>
              ))}
            </div>
          )}
          {r.catalyst_news.overall_sentiment === "no_news"
            && (r.catalyst_news.recent_catalysts?.length ?? 0) === 0 && (
            <div className="text-xs italic text-muted">
              No material catalysts found in the last 14 days — purely
              technical setup with no fundamental tailwind or drag.
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-yellow-500">
          ⚠ Catalyst news lookup failed (Gemini search grounding error).
        </div>
      )}

      {/* Entry + Invalidation */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="font-semibold mb-2">Entry plan</div>
          <div className="text-xs space-y-1">
            <div><span className="text-muted">Pullback target:</span> ${r.entry_plan_candidates?.pullback_target_price} ({r.entry_plan_candidates?.pullback_drop_needed_pct}% drop)</div>
            <div><span className="text-muted">Breakout level:</span> ${r.entry_plan_candidates?.breakout_level} (+{r.entry_plan_candidates?.breakout_dist_pct}%)</div>
            <div className="pt-1"><span className="text-muted">Preferred:</span> <strong>{r.entry_plan_candidates?.preferred_entry_note}</strong></div>
          </div>
        </div>
        <div>
          <div className="font-semibold mb-2">Invalidation</div>
          <div className="text-xs space-y-1">
            <div><span className="text-muted">Daily close below:</span> ${r.invalidation_levels?.daily_close_below}</div>
            <div><span className="text-muted">Weekly close below:</span> ${r.invalidation_levels?.weekly_close_below}</div>
            <div className="pt-1"><span className="text-muted">Hard stop:</span> ${r.invalidation_levels?.hard_stop_price} (-7%)</div>
          </div>
        </div>
      </div>

      {/* Missing data */}
      {r.missing_external_data?.length > 0 && (
        <div className="border-t pt-3">
          <div className="text-xs text-muted">
            Missing (need external APIs):{" "}
            {r.missing_external_data.map((m: string) => (
              <Badge key={m} tone="muted" className="mr-1">{m}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
